import { useState, useCallback } from 'react';
import { oauthService, OAuthTokens, UserProfile } from '@/services/oauthService';
import { toast } from 'sonner';

export interface UseOAuthReturn {
  isConnecting: boolean;
  connect: (platform: string) => Promise<void>;
  disconnect: (platform: string) => void;
  getUserProfile: (platform: string, accessToken: string) => Promise<UserProfile>;
  isPlatformConfigured: (platform: string) => boolean;
}

export function useOAuth(): UseOAuthReturn {
  const [isConnecting, setIsConnecting] = useState(false);

  const connect = useCallback(async (platform: string) => {
    
    if (!oauthService.isPlatformConfigured(platform)) {
      console.error(`[useOAuth] ${platform} OAuth is not configured`);
      toast.error(`${platform} OAuth is not configured. Please check your environment variables.`);
      return;
    }

    setIsConnecting(true);
    try {
      
      // Generate state for security
      const state = Math.random().toString(36).substring(2, 15);
      
      // Use sessionStorage for better reliability during redirects
      sessionStorage.setItem(`oauth_state_${platform}`, state);

      // Get authorization URL
      const authUrl = oauthService.getAuthUrl(platform, state);
      
      // Use direct redirect instead of popup to avoid CORS issues
      // Store the current pathname to return after OAuth
      localStorage.setItem('oauth_return_url', window.location.pathname);
      
      window.location.href = authUrl;

    } catch (error) {
      console.error(`OAuth connection error for ${platform}:`, error);
      toast.error(`Failed to connect to ${platform}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsConnecting(false);
    }
  }, []);


  const disconnect = useCallback((platform: string) => {
    // Remove stored data
    localStorage.removeItem(`${platform}_access_token`);
    localStorage.removeItem(`${platform}_refresh_token`);
    localStorage.removeItem(`${platform}_expires_at`);
    localStorage.removeItem(`${platform}_user_profile`);
    
    // Trigger a custom event for components to listen to
    window.dispatchEvent(new CustomEvent('oauth_disconnected', {
      detail: { platform }
    }));
    
    toast.success(`Disconnected from ${platform}`);
  }, []);

  const getUserProfile = useCallback(async (platform: string, accessToken: string): Promise<UserProfile> => {
    return await oauthService.getUserProfile(platform, accessToken);
  }, []);

  const isPlatformConfigured = useCallback((platform: string): boolean => {
    return oauthService.isPlatformConfigured(platform);
  }, []);

  return {
    isConnecting,
    connect,
    disconnect,
    getUserProfile,
    isPlatformConfigured
  };
}
